import { Component, EventEmitter, Input, Output } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { filter, single } from 'rxjs'
import { AnchorComponent, ButtonColor, ButtonDirection, ButtonDisplayType, ButtonSize } from '../button'
import { ApiResult } from '../remote-data'
import { ToastService } from '../toast'

/**
 * A very simple "Perform an action" button.
 *
 * This is designed for api requests on button clicks.
 * It can also navigate to a new page and show notifications.
 *
 * If you need a form, please use ftr-submit-button
 */
@Component({
  standalone: true,
  selector: 'ftr-remote-button',
  templateUrl: './remote-button.component.html',
  imports: [AnchorComponent],
})
export class RemoteButtonComponent<TResult> {
  // These are passed directly to the button component. See it for explanations
  @Input() color?: ButtonColor = ButtonColor.Secondary
  @Input() displayType?: ButtonDisplayType = ButtonDisplayType.Icon
  @Input() size?: ButtonSize
  @Input() fullWidth?: boolean = false
  @Input() direction?: ButtonDirection
  @Input() disabled?: boolean = false

  /**
   * The core of this component.
   *
   * On click, this button will
   */
  @Input()
  action: () => ApiResult<TResult>

  /**
   * Used to navigate after a successful submission.
   *
   * The route params are passed as part of router.navigate(...)
   */
  @Input()
  routerParams?: ((r: TResult) => string[]) | string[]

  /**
   * Used to display a notification when the submission completes
   */
  @Input()
  successText?: string | undefined

  /**
   * Used to display a notification when the submission fails
   */
  @Input()
  failureText?: string | undefined

  /**
   * If your failure coming back from the api is meant to be shown in the notification
   */
  @Input()
  useApiFailureText: boolean = false

  /**
   * Triggers when the action succeeds
   */
  @Output()
  onSuccess = new EventEmitter<TResult>()

  constructor(
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  /**
   * Handles a submission. Redirects if router params are provided on success.
   *
   * Note: we use single(), so if the observable emits more than one success/failure, we immediately throw an error.
   * This would imply we have a long-running observable, rather than purely an api response.
   */
  performAction(): void {
    if (this.disabled) {
      return
    }
    this.action()
      .pipe(
        filter(x => x.isCompleted()),
        single(),
      )
      .subscribe(remote => {
        if (remote.isSuccess()) {
          this.onSuccess.emit(remote._data)
          if (this.routerParams) {
            if (this.successText) {
              this.toastService.success(this.successText)
            }
            if (Array.isArray(this.routerParams)) {
              this.router.navigate(this.routerParams, { relativeTo: this.activatedRoute })
            } else {
              this.router.navigate(this.routerParams(remote._data), { relativeTo: this.activatedRoute })
            }
          } else {
            if (this.successText) {
              this.toastService.success(this.successText)
            }
          }
        } else if (remote.isFailure()) {
          const errorToShow = this.useApiFailureText ? remote._error.message : this.failureText
          if (errorToShow) {
            this.toastService.error(errorToShow)
          }
        }
      })
  }
}
